import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  ArrowUUpLeft,
  ArrowUUpRight,
  TextB,
  TextItalic,
  TextUnderline,
  TextStrikethrough,
  TextAlignLeft,
  TextAlignCenter,
  TextAlignRight,
  TextAlignJustify,
  ListBullets,
  ListNumbers,
  ListChecks,
  Link as LinkIcon,
  Image as ImageIcon,
  Table as TableIcon,
  Minus,
  TextSuperscript,
  TextSubscript,
  Palette,
  HighlighterCircle,
  Eraser,
  CaretDown,
  TextIndent,
  TextOutdent,
  Quotes,
  Code,
  LineSegment,
  ImageSquare,
  Upload,
} from "@phosphor-icons/react";
import { useEditorStore } from "../../stores/editor";
import { Tooltip } from "../ui/Tooltip";
import { cn } from "../../lib/cn";

const spring = { type: "spring" as const, stiffness: 500, damping: 35, mass: 0.6 };

function TBtn({
  onClick,
  label,
  active,
  disabled,
  children,
}: {
  onClick: () => void;
  label: string;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Tooltip content={label} placement="bottom" delay={400}>
      <motion.button
        onClick={onClick}
        disabled={disabled}
        whileHover={disabled ? {} : { scale: 1.1 }}
        whileTap={disabled ? {} : { scale: 0.9 }}
        transition={spring}
        aria-label={label}
        className={cn(
          "flex h-[28px] w-[28px] items-center justify-center rounded-[var(--radius-sm)]",
          "transition-colors duration-75",
          disabled
            ? "text-[var(--text-muted)] opacity-30 cursor-not-allowed"
            : active
              ? "bg-[var(--bg-surface-hover)] text-[var(--text-primary)]"
              : "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
        )}
      >
        {children}
      </motion.button>
    </Tooltip>
  );
}

function Divider() {
  return <div className="mx-0.5 h-5 w-px bg-[var(--border-hairline)] shrink-0" />;
}

interface DropdownOption {
  label: string;
  value: string;
  style?: React.CSSProperties;
}

function ToolbarDropdown({
  options,
  value,
  onChange,
  label,
  width = 100,
  renderLabel,
}: {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  label: string;
  width?: number;
  renderLabel?: (option: DropdownOption | undefined) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const current = options.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative">
      <Tooltip content={label} placement="bottom" delay={400} disabled={open}>
        <button
          onClick={() => setOpen(!open)}
          className={cn(
            "flex h-[28px] items-center gap-0.5 rounded-[var(--radius-sm)] px-2",
            "text-[12px] font-medium text-[var(--text-secondary)]",
            "hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
            "transition-colors duration-75",
            open && "bg-[var(--bg-surface-hover)] text-[var(--text-primary)]",
          )}
          style={{ minWidth: width }}
        >
          <span className="truncate">
            {renderLabel ? renderLabel(current) : (current?.label ?? label)}
          </span>
          <CaretDown size={10} weight="bold" className="shrink-0 opacity-50" />
        </button>
      </Tooltip>
      {open && (
        <div
          className={cn(
            "absolute top-full left-0 z-[100] mt-1 max-h-[320px] overflow-y-auto",
            "rounded-[var(--radius-lg)] border border-[var(--border-hairline)]",
            "bg-[var(--bg-surface)] shadow-[var(--shadow-lg)] backdrop-blur-[var(--blur-lg)]",
            "py-1",
          )}
          style={{ minWidth: Math.max(width + 40, 140) }}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={cn(
                "flex w-full items-center px-3 py-1.5 text-[12px]",
                "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
                opt.value === value
                  ? "text-[var(--text-primary)] font-semibold"
                  : "text-[var(--text-secondary)]",
              )}
              style={opt.style}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ColorPicker({
  label,
  icon: Icon,
  currentColor,
  onColorChange,
  onClear,
  disabled,
}: {
  label: string;
  icon: typeof Palette;
  currentColor: string | undefined;
  onColorChange: (color: string) => void;
  onClear?: () => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const colors = [
    "#000000", "#434343", "#666666", "#999999", "#b7b7b7",
    "#cccccc", "#d9d9d9", "#efefef", "#f3f3f3", "#ffffff",
    "#980000", "#ff0000", "#ff9900", "#ffff00", "#00ff00",
    "#00ffff", "#4a86e8", "#0000ff", "#9900ff", "#ff00ff",
    "#e6b8af", "#f4cccc", "#fce5cd", "#fff2cc", "#d9ead3",
    "#d0e0e3", "#c9daf8", "#cfe2f3", "#d9d2e9", "#ead1dc",
    "#dd7e6b", "#ea9999", "#f9cb9c", "#ffe599", "#b6d7a8",
    "#a2c4c9", "#a4c2f4", "#9fc5e8", "#b4a7d6", "#d5a6bd",
    "#cc4125", "#e06666", "#f6b26b", "#ffd966", "#93c47d",
    "#76a5af", "#6d9eeb", "#6fa8dc", "#8e7cc3", "#c27ba0",
    "#a61c00", "#cc0000", "#e69138", "#f1c232", "#6aa84f",
    "#45818e", "#3c78d8", "#3d85c6", "#674ea7", "#a64d79",
    "#85200c", "#990000", "#b45f06", "#bf9000", "#38761d",
    "#134f5c", "#1155cc", "#0b5394", "#351c75", "#741b47",
  ];

  return (
    <div ref={ref} className="relative">
      <Tooltip content={label} placement="bottom" delay={400} disabled={open}>
        <button
          onClick={() => !disabled && setOpen(!open)}
          disabled={disabled}
          className={cn(
            "flex h-[28px] w-[28px] flex-col items-center justify-center rounded-[var(--radius-sm)]",
            "transition-colors duration-75",
            disabled
              ? "text-[var(--text-muted)] opacity-30 cursor-not-allowed"
              : open
                ? "bg-[var(--bg-surface-hover)] text-[var(--text-primary)]"
                : "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
          )}
        >
          <Icon size={14} weight="bold" />
          <div
            className="h-[3px] w-[14px] rounded-full mt-px"
            style={{ backgroundColor: currentColor || "#000000" }}
          />
        </button>
      </Tooltip>
      {open && (
        <div
          className={cn(
            "absolute top-full left-1/2 -translate-x-1/2 z-[100] mt-1 p-2",
            "rounded-[var(--radius-lg)] border border-[var(--border-hairline)]",
            "bg-[var(--bg-surface)] shadow-[var(--shadow-lg)] backdrop-blur-[var(--blur-lg)]",
          )}
        >
          <div className="grid grid-cols-10 gap-[3px]" style={{ width: 208 }}>
            {colors.map((color) => (
              <button
                key={color}
                onClick={() => { onColorChange(color); setOpen(false); }}
                className={cn(
                  "h-[18px] w-[18px] rounded-[2px] transition-transform hover:scale-125",
                  color === "#ffffff" && "border border-[var(--border-default)]",
                )}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
          {onClear && (
            <button
              onClick={() => { onClear(); setOpen(false); }}
              className="mt-2 w-full rounded-[var(--radius-sm)] px-2 py-1 text-[11px] text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)] transition-colors text-center"
            >
              Remove color
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ImageInsertPopover({
  disabled,
  onInsertUrl,
  onInsertFile,
}: {
  disabled: boolean;
  onInsertUrl: (url: string) => void;
  onInsertFile: (file: File) => void;
}) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <Tooltip content="Insert image" placement="bottom" delay={400} disabled={open}>
        <motion.button
          onClick={() => !disabled && setOpen(!open)}
          disabled={disabled}
          whileHover={disabled ? {} : { scale: 1.1 }}
          whileTap={disabled ? {} : { scale: 0.9 }}
          transition={spring}
          aria-label="Insert image"
          className={cn(
            "flex h-[28px] w-[28px] items-center justify-center rounded-[var(--radius-sm)]",
            "transition-colors duration-75",
            disabled
              ? "text-[var(--text-muted)] opacity-30 cursor-not-allowed"
              : open
                ? "bg-[var(--bg-surface-hover)] text-[var(--text-primary)]"
                : "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
          )}
        >
          <ImageIcon size={14} weight="bold" />
        </motion.button>
      </Tooltip>
      {open && (
        <div
          className={cn(
            "absolute top-full right-0 z-[100] mt-1 p-3 w-[280px]",
            "rounded-[var(--radius-lg)] border border-[var(--border-hairline)]",
            "bg-[var(--bg-surface)] shadow-[var(--shadow-lg)] backdrop-blur-[var(--blur-lg)]",
          )}
        >
          <p className="text-[11px] font-medium text-[var(--text-secondary)] mb-2">
            Insert image
          </p>

          <button
            onClick={() => fileRef.current?.click()}
            className={cn(
              "w-full flex items-center justify-center gap-2 px-3 py-2 mb-2",
              "rounded-[var(--radius-sm)] border border-dashed border-[var(--border-default)]",
              "text-[12px] text-[var(--text-secondary)]",
              "hover:border-[var(--color-lever-orange)] hover:text-[var(--color-lever-orange)]",
              "hover:bg-[var(--color-lever-orange)]/5 transition-all",
            )}
          >
            <Upload size={14} weight="bold" />
            Upload from computer
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                onInsertFile(file);
                setOpen(false);
              }
            }}
          />

          <div className="flex items-center gap-2 my-2">
            <div className="flex-1 h-px bg-[var(--border-hairline)]" />
            <span className="text-[10px] text-[var(--text-muted)]">or</span>
            <div className="flex-1 h-px bg-[var(--border-hairline)]" />
          </div>

          <div className="flex items-center gap-1.5">
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste image URL..."
              className={cn(
                "flex-1 h-[28px] rounded-[var(--radius-sm)] border border-[var(--border-default)] px-2",
                "text-[12px] text-[var(--text-primary)] bg-[var(--bg-primary)]",
                "placeholder:text-[var(--text-muted)]",
                "focus:outline-none focus:border-[var(--color-lever-orange)]",
              )}
              onKeyDown={(e) => {
                if (e.key === "Enter" && url.trim()) {
                  onInsertUrl(url.trim());
                  setUrl("");
                  setOpen(false);
                }
              }}
            />
            <button
              onClick={() => {
                if (url.trim()) {
                  onInsertUrl(url.trim());
                  setUrl("");
                  setOpen(false);
                }
              }}
              disabled={!url.trim()}
              className={cn(
                "h-[28px] px-2 rounded-[var(--radius-sm)] text-[11px] font-medium",
                "bg-[var(--text-primary)] text-[var(--text-inverse)]",
                "hover:opacity-90 transition-opacity disabled:opacity-30",
              )}
            >
              Add
            </button>
          </div>

          <p className="text-[10px] text-[var(--text-muted)] mt-2 flex items-center gap-1">
            <ImageSquare size={10} />
            Drag & drop images directly into the document
          </p>
        </div>
      )}
    </div>
  );
}

const HEADING_OPTIONS: DropdownOption[] = [
  { label: "Normal text", value: "paragraph" },
  { label: "Heading 1", value: "1", style: { fontSize: "18pt", fontWeight: 700 } },
  { label: "Heading 2", value: "2", style: { fontSize: "16pt", fontWeight: 700 } },
  { label: "Heading 3", value: "3", style: { fontSize: "14pt", fontWeight: 700 } },
  { label: "Heading 4", value: "4", style: { fontSize: "12pt", fontWeight: 700 } },
  { label: "Heading 5", value: "5", style: { fontSize: "11pt", fontWeight: 700 } },
  { label: "Heading 6", value: "6", style: { fontSize: "10pt", fontWeight: 700 } },
];

const FONT_FAMILY_OPTIONS: DropdownOption[] = [
  { label: "Arial", value: "" },
  { label: "Courier New", value: "'Courier New', Courier, monospace", style: { fontFamily: "'Courier New', Courier, monospace" } },
  { label: "Georgia", value: "Georgia, serif", style: { fontFamily: "Georgia, serif" } },
  { label: "Garamond", value: "Garamond, serif", style: { fontFamily: "Garamond, serif" } },
  { label: "Helvetica", value: "Helvetica, Arial, sans-serif", style: { fontFamily: "Helvetica, Arial, sans-serif" } },
  { label: "Lora", value: "Lora, Georgia, serif", style: { fontFamily: "Lora, Georgia, serif" } },
  { label: "Roboto", value: "Roboto, Arial, sans-serif", style: { fontFamily: "Roboto, Arial, sans-serif" } },
  { label: "Times New Roman", value: "'Times New Roman', Times, serif", style: { fontFamily: "'Times New Roman', Times, serif" } },
  { label: "Trebuchet MS", value: "'Trebuchet MS', sans-serif", style: { fontFamily: "'Trebuchet MS', sans-serif" } },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif", style: { fontFamily: "Verdana, Geneva, sans-serif" } },
];

const FONT_SIZE_OPTIONS: DropdownOption[] = [
  { label: "8", value: "8pt" },
  { label: "9", value: "9pt" },
  { label: "10", value: "10pt" },
  { label: "11", value: "11pt" },
  { label: "12", value: "12pt" },
  { label: "14", value: "14pt" },
  { label: "16", value: "16pt" },
  { label: "18", value: "18pt" },
  { label: "20", value: "20pt" },
  { label: "22", value: "22pt" },
  { label: "24", value: "24pt" },
  { label: "26", value: "26pt" },
  { label: "28", value: "28pt" },
  { label: "36", value: "36pt" },
  { label: "48", value: "48pt" },
  { label: "72", value: "72pt" },
  { label: "96", value: "96pt" },
];

const LINE_SPACING_OPTIONS: DropdownOption[] = [
  { label: "Single (1.0)", value: "1" },
  { label: "1.15", value: "1.15" },
  { label: "1.5", value: "1.5" },
  { label: "Double (2.0)", value: "2" },
  { label: "2.5", value: "2.5" },
  { label: "Triple (3.0)", value: "3" },
];

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function DocxToolbar() {
  const docxEditor = useEditorStore((s) => s.docxEditor);
  const disabled = !docxEditor;

  const [, forceUpdate] = useState(0);
  useEffect(() => {
    if (!docxEditor) return;
    const handler = () => forceUpdate((c) => c + 1);
    docxEditor.on("transaction", handler);
    return () => { docxEditor.off("transaction", handler); };
  }, [docxEditor]);

  const run = useCallback(
    (fn: () => void) => {
      if (!docxEditor) return;
      fn();
    },
    [docxEditor],
  );

  const getCurrentHeading = (): string => {
    if (!docxEditor) return "paragraph";
    for (let i = 1; i <= 6; i++) {
      if (docxEditor.isActive("heading", { level: i })) return String(i);
    }
    return "paragraph";
  };

  const getCurrentFontFamily = (): string => {
    if (!docxEditor) return "";
    return docxEditor.getAttributes("textStyle").fontFamily || "";
  };

  const getCurrentFontSize = (): string => {
    if (!docxEditor) return "11pt";
    return docxEditor.getAttributes("textStyle").fontSize || "11pt";
  };

  const getCurrentLineSpacing = (): string => {
    if (!docxEditor) return "1.15";
    const para = docxEditor.getAttributes("paragraph");
    const heading = docxEditor.getAttributes("heading");
    return para.lineSpacing || heading.lineSpacing || "1.15";
  };

  const handleHeadingChange = useCallback(
    (value: string) => {
      if (!docxEditor) return;
      if (value === "paragraph") {
        docxEditor.chain().focus().setParagraph().run();
      } else {
        const level = parseInt(value) as 1 | 2 | 3 | 4 | 5 | 6;
        docxEditor.chain().focus().toggleHeading({ level }).run();
      }
    },
    [docxEditor],
  );

  const handleFontFamilyChange = useCallback(
    (value: string) => {
      if (!docxEditor) return;
      if (value === "") {
        docxEditor.chain().focus().unsetFontFamily().run();
      } else {
        docxEditor.chain().focus().setFontFamily(value).run();
      }
    },
    [docxEditor],
  );

  const handleFontSizeChange = useCallback(
    (value: string) => {
      if (!docxEditor) return;
      docxEditor.chain().focus().setFontSize(value).run();
    },
    [docxEditor],
  );

  const handleLineSpacingChange = useCallback(
    (value: string) => {
      if (!docxEditor) return;
      docxEditor.chain().focus().setLineSpacing(value).run();
    },
    [docxEditor],
  );

  const handleInsertLink = useCallback(() => {
    if (!docxEditor) return;
    const previousUrl = docxEditor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);
    if (url === null) return;
    if (url === "") {
      docxEditor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    docxEditor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [docxEditor]);

  const handleInsertImageUrl = useCallback((url: string) => {
    if (!docxEditor) return;
    docxEditor.chain().focus().setImage({ src: url, alignment: "center" }).run();
  }, [docxEditor]);

  const handleInsertImageFile = useCallback(async (file: File) => {
    if (!docxEditor) return;
    const dataUrl = await readFileAsBase64(file);
    docxEditor.chain().focus().setImage({ src: dataUrl, alignment: "center" }).run();
  }, [docxEditor]);

  const handleInsertTable = useCallback(() => {
    if (!docxEditor) return;
    docxEditor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [docxEditor]);

  const fontFamilyLabel = (opt: DropdownOption | undefined) => {
    if (!opt || opt.value === "") return "Arial";
    return opt.label;
  };

  const fontSizeLabel = (opt: DropdownOption | undefined) => {
    if (!opt) return "11";
    return opt.label;
  };

  const lineSpacingLabel = (opt: DropdownOption | undefined) => {
    if (!opt) return "1.15";
    return opt.value;
  };

  return (
    <div
      className={cn(
        "flex items-center gap-0.5 px-2 py-1 shrink-0",
        "border-b border-[var(--border-hairline)] bg-[var(--bg-surface)]",
        "flex-wrap",
      )}
    >
      {/* Undo / Redo */}
      <TBtn label="Undo (⌘Z)" disabled={disabled} onClick={() => run(() => docxEditor!.chain().focus().undo().run())}>
        <ArrowUUpLeft size={14} weight="bold" />
      </TBtn>
      <TBtn label="Redo (⌘⇧Z)" disabled={disabled} onClick={() => run(() => docxEditor!.chain().focus().redo().run())}>
        <ArrowUUpRight size={14} weight="bold" />
      </TBtn>

      <Divider />

      {/* Heading dropdown */}
      <ToolbarDropdown
        options={HEADING_OPTIONS}
        value={getCurrentHeading()}
        onChange={handleHeadingChange}
        label="Styles"
        width={100}
      />

      <Divider />

      {/* Font family */}
      <ToolbarDropdown
        options={FONT_FAMILY_OPTIONS}
        value={getCurrentFontFamily()}
        onChange={handleFontFamilyChange}
        label="Font"
        width={110}
        renderLabel={fontFamilyLabel}
      />

      {/* Font size */}
      <ToolbarDropdown
        options={FONT_SIZE_OPTIONS}
        value={getCurrentFontSize()}
        onChange={handleFontSizeChange}
        label="Font size"
        width={48}
        renderLabel={fontSizeLabel}
      />

      <Divider />

      {/* Text formatting */}
      <TBtn
        label="Bold (⌘B)"
        disabled={disabled}
        active={docxEditor?.isActive("bold")}
        onClick={() => run(() => docxEditor!.chain().focus().toggleBold().run())}
      >
        <TextB size={14} weight="bold" />
      </TBtn>
      <TBtn
        label="Italic (⌘I)"
        disabled={disabled}
        active={docxEditor?.isActive("italic")}
        onClick={() => run(() => docxEditor!.chain().focus().toggleItalic().run())}
      >
        <TextItalic size={14} weight="bold" />
      </TBtn>
      <TBtn
        label="Underline (⌘U)"
        disabled={disabled}
        active={docxEditor?.isActive("underline")}
        onClick={() => run(() => docxEditor!.chain().focus().toggleUnderline().run())}
      >
        <TextUnderline size={14} weight="bold" />
      </TBtn>
      <TBtn
        label="Strikethrough"
        disabled={disabled}
        active={docxEditor?.isActive("strike")}
        onClick={() => run(() => docxEditor!.chain().focus().toggleStrike().run())}
      >
        <TextStrikethrough size={14} weight="bold" />
      </TBtn>
      <TBtn
        label="Superscript"
        disabled={disabled}
        active={docxEditor?.isActive("superscript")}
        onClick={() => run(() => docxEditor!.chain().focus().toggleSuperscript().run())}
      >
        <TextSuperscript size={14} weight="bold" />
      </TBtn>
      <TBtn
        label="Subscript"
        disabled={disabled}
        active={docxEditor?.isActive("subscript")}
        onClick={() => run(() => docxEditor!.chain().focus().toggleSubscript().run())}
      >
        <TextSubscript size={14} weight="bold" />
      </TBtn>

      <Divider />

      {/* Colors */}
      <ColorPicker
        label="Text color"
        icon={Palette}
        currentColor={docxEditor?.getAttributes("textStyle").color}
        onColorChange={(color) => run(() => docxEditor!.chain().focus().setColor(color).run())}
        onClear={() => run(() => docxEditor!.chain().focus().unsetColor().run())}
        disabled={disabled}
      />
      <ColorPicker
        label="Highlight color"
        icon={HighlighterCircle}
        currentColor={docxEditor?.getAttributes("highlight").color}
        onColorChange={(color) => run(() => docxEditor!.chain().focus().toggleHighlight({ color }).run())}
        onClear={() => run(() => docxEditor!.chain().focus().unsetHighlight().run())}
        disabled={disabled}
      />

      <Divider />

      {/* Alignment */}
      <TBtn
        label="Align left"
        disabled={disabled}
        active={docxEditor?.isActive({ textAlign: "left" })}
        onClick={() => run(() => docxEditor!.chain().focus().setTextAlign("left").run())}
      >
        <TextAlignLeft size={14} weight="bold" />
      </TBtn>
      <TBtn
        label="Center"
        disabled={disabled}
        active={docxEditor?.isActive({ textAlign: "center" })}
        onClick={() => run(() => docxEditor!.chain().focus().setTextAlign("center").run())}
      >
        <TextAlignCenter size={14} weight="bold" />
      </TBtn>
      <TBtn
        label="Align right"
        disabled={disabled}
        active={docxEditor?.isActive({ textAlign: "right" })}
        onClick={() => run(() => docxEditor!.chain().focus().setTextAlign("right").run())}
      >
        <TextAlignRight size={14} weight="bold" />
      </TBtn>
      <TBtn
        label="Justify"
        disabled={disabled}
        active={docxEditor?.isActive({ textAlign: "justify" })}
        onClick={() => run(() => docxEditor!.chain().focus().setTextAlign("justify").run())}
      >
        <TextAlignJustify size={14} weight="bold" />
      </TBtn>

      <Divider />

      {/* Line spacing */}
      <Tooltip content="Line spacing" placement="bottom" delay={400}>
        <div className="flex items-center">
          <LineSegment size={14} weight="bold" className="text-[var(--text-muted)] mr-0.5" />
          <ToolbarDropdown
            options={LINE_SPACING_OPTIONS}
            value={getCurrentLineSpacing()}
            onChange={handleLineSpacingChange}
            label="Line spacing"
            width={48}
            renderLabel={lineSpacingLabel}
          />
        </div>
      </Tooltip>

      <Divider />

      {/* Lists */}
      <TBtn
        label="Bullet list"
        disabled={disabled}
        active={docxEditor?.isActive("bulletList")}
        onClick={() => run(() => docxEditor!.chain().focus().toggleBulletList().run())}
      >
        <ListBullets size={14} weight="bold" />
      </TBtn>
      <TBtn
        label="Numbered list"
        disabled={disabled}
        active={docxEditor?.isActive("orderedList")}
        onClick={() => run(() => docxEditor!.chain().focus().toggleOrderedList().run())}
      >
        <ListNumbers size={14} weight="bold" />
      </TBtn>
      <TBtn
        label="Task list"
        disabled={disabled}
        active={docxEditor?.isActive("taskList")}
        onClick={() => run(() => docxEditor!.chain().focus().toggleTaskList().run())}
      >
        <ListChecks size={14} weight="bold" />
      </TBtn>

      <Divider />

      {/* Indent */}
      <TBtn
        label="Decrease indent"
        disabled={disabled}
        onClick={() => run(() => docxEditor!.chain().focus().liftListItem("listItem").run())}
      >
        <TextOutdent size={14} weight="bold" />
      </TBtn>
      <TBtn
        label="Increase indent"
        disabled={disabled}
        onClick={() => run(() => docxEditor!.chain().focus().sinkListItem("listItem").run())}
      >
        <TextIndent size={14} weight="bold" />
      </TBtn>

      <Divider />

      {/* Block elements */}
      <TBtn
        label="Block quote"
        disabled={disabled}
        active={docxEditor?.isActive("blockquote")}
        onClick={() => run(() => docxEditor!.chain().focus().toggleBlockquote().run())}
      >
        <Quotes size={14} weight="bold" />
      </TBtn>
      <TBtn
        label="Code block"
        disabled={disabled}
        active={docxEditor?.isActive("codeBlock")}
        onClick={() => run(() => docxEditor!.chain().focus().toggleCodeBlock().run())}
      >
        <Code size={14} weight="bold" />
      </TBtn>

      <Divider />

      {/* Insert */}
      <TBtn label="Insert link" disabled={disabled} onClick={handleInsertLink} active={docxEditor?.isActive("link")}>
        <LinkIcon size={14} weight="bold" />
      </TBtn>
      <ImageInsertPopover
        disabled={disabled}
        onInsertUrl={handleInsertImageUrl}
        onInsertFile={handleInsertImageFile}
      />
      <TBtn label="Insert table" disabled={disabled} onClick={handleInsertTable}>
        <TableIcon size={14} weight="bold" />
      </TBtn>
      <TBtn
        label="Horizontal rule"
        disabled={disabled}
        onClick={() => run(() => docxEditor!.chain().focus().setHorizontalRule().run())}
      >
        <Minus size={14} weight="bold" />
      </TBtn>

      <Divider />

      {/* Clear formatting */}
      <TBtn
        label="Clear formatting"
        disabled={disabled}
        onClick={() => run(() => docxEditor!.chain().focus().unsetAllMarks().clearNodes().run())}
      >
        <Eraser size={14} weight="bold" />
      </TBtn>
    </div>
  );
}
